import moment from 'moment'

// Shared status rules for an employee's mandatory training records. Mirrors
// the server's helpers/training/complianceStatus.js (and the mobile copy in
// mobile/src/utils/mandatoryTrainingStatus.js) - keep all three in step.
//
// A record is valid until its own expiry date when it has one (e-learning
// completions do), otherwise for the course frequency from its completion
// date. "Expiring soon" is the 6 weeks before that. Results from the retired
// browser-scored e-learning are history only and never count.
//
// The training matrix and My Training take their statuses from the server
// (/training/admin/matrix, /training/my-courses), which also applies pathway
// levels and cross-course cover; this helper is for showing one person's
// records.

export const FREQUENCY_MONTHS = {
  Annually: 12,
  'Two Years': 24,
  'Three Years': 36,
}

export const EXPIRING_SOON_WEEKS = 6

export const STATUS = {
  compliant: { key: 'compliant', label: 'Compliant', color: 'success' },
  expiring: { key: 'expiring', label: 'Expiring Soon', color: 'warning' },
  overdue: { key: 'overdue', label: 'Overdue', color: 'error' },
  notCompleted: { key: 'notCompleted', label: 'Not Completed', color: 'error' },
  pending: { key: 'pending', label: 'Awaiting Approval', color: 'info' },
  rejected: { key: 'rejected', label: 'Rejected', color: 'default' },
  legacy: { key: 'legacy', label: 'Not Counted', color: 'default' },
}

export const isELearningRecord = (record) =>
  !!(record?.eLearning?.courseName || record?.eLearning?.percentage != null)

// A result from the retired e-learning that has been replaced by a
// catalogue course, so it no longer counts. Old results for courses with no
// replacement yet still count (the server marks them when the course is
// linked).
export const isLegacyELearning = (record) => record?.source === 'legacyElearning'

export const getExpiryDate = (course, record) => {
  if (record?.expiresAt) return moment(record.expiresAt)
  const months = FREQUENCY_MONTHS[course?.frequency]
  if (!months || !record?.date) return null
  return moment(record.date).add(months, 'months')
}

// Status of one record against its course
export const getRecordStatus = (course, record) => {
  if (!record) return STATUS.notCompleted
  if (record.rejected) return STATUS.rejected
  if (isLegacyELearning(record)) return STATUS.legacy
  if (record.requiresApproval) return STATUS.pending
  const expiry = getExpiryDate(course, record)
  if (!expiry) return STATUS.compliant // unknown frequency = never expires
  if (expiry.isBefore(moment())) return STATUS.overdue
  if (expiry.isBefore(moment().add(EXPIRING_SOON_WEEKS, 'weeks'))) {
    return STATUS.expiring
  }
  return STATUS.compliant
}

// Most recent record for a course that can count (the one the status is
// read from). Pending uploads are included, so a renewal awaiting approval
// shows as such; rejected and legacy e-learning records are not.
export const getLatestRecord = (records = [], courseID) =>
  records
    .filter(
      (record) =>
        !record.rejected &&
        !isLegacyELearning(record) &&
        String(record.course?._id ?? record.course) === String(courseID),
    )
    .sort((a, b) => moment(b.date) - moment(a.date))[0]

// Status of a course for an employee, from all their records
export const getCourseStatus = (course, records = []) => {
  const latest = getLatestRecord(records, course._id)
  return { latest, ...getRecordStatus(course, latest) }
}
